/**
 * What is known about how a player bats and bowls, at the moment the scorer is asked (ADR-0023).
 *
 * Two sources, and the order between them is the point. **This match's ledger comes first**: if
 * Rahul already walked out left-handed this innings, or Ravi already bowled spin earlier, that is
 * what was true, and it is what the next walk-in or over is pre-filled with. Only when the match
 * has not said does the player's **saved default** speak, which is what carries a left-hander
 * from last Saturday's match into this one. When neither knows, the answer is `null` and the
 * scorer is asked, once; a guess is never made on anyone's behalf.
 *
 * Pure, so the decision of what to pre-fill is tested and not left to a component.
 */
import type { BatterHand, BowlingArm, BowlingType, MatchState, PlayerId } from "@sportsos/engine";
import type { MatchSetup } from "./setup.ts";

/** An arm and a type, either of which may still be unknown. */
export interface KnownBowling {
  readonly arm: BowlingArm | null;
  readonly type: BowlingType | null;
}

/** A bowler's style once it is complete: what a bowler sheet hands back. */
export interface BowlingChoice {
  readonly arm: BowlingArm;
  readonly type: BowlingType;
}

const squadPlayer = (setup: MatchSetup, playerId: PlayerId) => {
  for (const team of setup.teams) {
    const found = team.players.find((player) => player.id === playerId);
    if (found !== undefined) return found;
  }
  return undefined;
};

/**
 * Which way he stands, or null if nobody has said. The latest innings of this match that recorded
 * it wins; the saved default is the fallback.
 */
export function knownBattingHand(setup: MatchSetup, state: MatchState, playerId: PlayerId): BatterHand | null {
  for (let i = state.innings.length - 1; i >= 0; i -= 1) {
    const line = state.innings[i]?.batting.find((entry) => entry.playerId === playerId);
    if (line?.hand != null) return line.hand;
  }
  return squadPlayer(setup, playerId)?.battingHand ?? null;
}

/**
 * The arm and type he bowled most recently in this match, each on its own, with the saved default
 * filling whatever the match has not said. A bowler who switched type between overs is
 * pre-filled with the later one, which is what he is most likely to bowl next.
 */
export function knownBowling(setup: MatchSetup, state: MatchState, playerId: PlayerId): KnownBowling {
  let arm: BowlingArm | null = null;
  let type: BowlingType | null = null;
  for (let i = state.innings.length - 1; i >= 0 && (arm === null || type === null); i -= 1) {
    const line = state.innings[i]?.bowling.find((entry) => entry.playerId === playerId);
    if (line === undefined) continue;
    arm ??= line.arm;
    type ??= line.type;
  }
  const saved = squadPlayer(setup, playerId);
  return { arm: arm ?? saved?.bowlingArm ?? null, type: type ?? saved?.bowlingType ?? null };
}

/** The whole style, or null if either half is still unknown. */
export const completeBowling = (known: KnownBowling): BowlingChoice | null =>
  known.arm !== null && known.type !== null ? { arm: known.arm, type: known.type } : null;

/**
 * How far a hand correction reaches, for the warning the scorer sees before it is applied.
 *
 * A correction supersedes the batter's walk-in, so it takes effect from his arrival: every ball
 * he faced this innings, and every shot placed on one of them, is read with the new hand. The
 * score does not change; where those shots are drawn does.
 */
export function handChangeImpact(
  state: MatchState,
  inningsNo: number,
  playerId: PlayerId,
): { readonly balls: number; readonly shots: number } {
  const innings = state.innings.find((entry) => entry.inningsNo === inningsNo);
  const faced = innings?.deliveries.filter((ball) => ball.batterId === playerId) ?? [];
  return { balls: faced.length, shots: faced.filter((ball) => ball.shot !== null).length };
}
